import type {ReactNode} from 'react';

import type {AfriexBusinessTransaction} from '@/types/AfriexBusinessTransaction';
import {BusinessTransactionStatus} from '@/types/AfriexBusinessTransactionStatus';
import {BusinessTransactionType} from '@/types/AfriexBusinessTransactionType';
import {formatValues, getBusinessTxAmountAndAsset} from '@/utils/dashboard';

const formatMerchantStatementValues = (
  transaction: AfriexBusinessTransaction,
): {
  description: string;
  credit: ReactNode;
  debit: ReactNode;
  balance: ReactNode;
} => {
  const {
    txType,
    initiatedAmount,
    initiatedAsset,
    fulfilledAmount,
    fulfilledAsset,
  } = getBusinessTxAmountAndAsset(transaction);

  const description = transaction.type;

  // The wallet balance the ledger recorded with the row, in the currency it
  // moved: the credited one for a credit, the debited one otherwise. A failed,
  // refunded or voided debit never moved the wallet, so it shows the balance
  // before it, as the business CSV and the statement PDF do. "-" when nothing
  // was recorded.
  const ledgerMeta = transaction.meta?.ledgerMeta;
  const isDebitReversed =
    transaction.status === BusinessTransactionStatus.FAILED ||
    transaction.status === BusinessTransactionStatus.REFUNDED ||
    (ledgerMeta?.voidTransactionIds?.length ?? 0) > 0;
  const snapshot: Partial<Record<string, number>> | undefined = isDebitReversed
    ? ledgerMeta?.balanceBefore
    : ledgerMeta?.balanceAfter;
  const balanceIn = (currency: string | undefined): ReactNode => {
    const value = currency ? snapshot?.[currency] : undefined;
    return value === undefined ? (
      '-'
    ) : (
      <>
        {formatValues(Number(value), 'money')} {currency}
      </>
    );
  };

  switch (txType) {
    case BusinessTransactionType.DEPOSIT:
    case BusinessTransactionType.REVERSAL:
      return {
        description: description,
        credit: (
          <>
            {fulfilledAmount} {fulfilledAsset}
          </>
        ),
        debit: '',
        balance: balanceIn(fulfilledAsset),
      };
    case BusinessTransactionType.WITHDRAW:
    case BusinessTransactionType.SEND:
      return {
        description: description,
        credit: '',
        debit: (
          <>
            {initiatedAmount} {initiatedAsset}
          </>
        ),
        balance: balanceIn(initiatedAsset),
      };
    case BusinessTransactionType.SWAP:
      return {
        description: description,
        credit: (
          <>
            {fulfilledAmount} {fulfilledAsset}
          </>
        ),
        debit: (
          <>
            {initiatedAmount} {initiatedAsset}
          </>
        ),
        balance: balanceIn(initiatedAsset),
      };
    default:
      return {
        description: description,
        credit: (
          <>
            {fulfilledAmount} {fulfilledAsset}
          </>
        ),
        debit: '',
        balance: balanceIn(fulfilledAsset),
      };
  }
};

export default formatMerchantStatementValues;
